@if (row; as r) {
<!-- Native <dialog> for a11y; kept open via the attribute so Angular owns the lifecycle. -->
<dialog
  open
  class="fixed inset-0 z-[60] m-0 flex h-full max-h-none w-full max-w-none border-0 bg-transparent p-0 !mt-0"
  [attr.aria-label]="title"
>
  <button type="button" class="flex-1 cursor-default bg-black/40" aria-label="Close" (click)="closed.emit()"></button>
  <aside class="flex w-full max-w-2xl flex-col bg-white text-black shadow-2xl dark:bg-[#0e1726] dark:text-white">
    <!-- Header -->
    <div class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-[#191e3a]">
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-2">
          <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" [class]="chipClass">{{ title }}</span>
          <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" [class]="outcomeClass">
            {{ r.outcome === 'failure' ? 'Failed' : 'Done' }}
          </span>
        </div>
        <h2 class="mt-2 truncate text-lg font-semibold">{{ record }}</h2>
        <p class="text-sm text-gray-500">
          {{ r.created_at | date : 'EEEE, dd MMM y' }} at {{ r.created_at | date : 'HH:mm:ss' }}
        </p>
      </div>
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
        aria-label="Close"
        (click)="closed.emit()"
      >
        <icon-x class="h-4 w-4" />
      </button>
    </div>

    <div class="flex-1 space-y-6 overflow-y-auto px-6 py-5">
      @if (errorInfo; as e) {
      <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
        <span class="font-semibold">Request failed ({{ e.status }}).</span> {{ e.message }}
      </div>
      }

      <!-- What changed -->
      <section>
        <h3 class="mb-2 text-sm font-semibold">
          @switch (snapshotKind) {
            @case ('update') { What changed }
            @case ('create') { Record as created }
            @case ('delete') { Record as it was }
            @default { What changed }
          }
        </h3>
        @if (snapshotKind === 'none') {
        <p class="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-white/5">
          No field-level snapshot for this action.
        </p>
        } @else if (snapshotKind === 'update' && changed.length === 0) {
        <p class="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-white/5">
          Saved without any field changes.
        </p>
        } @else {
        <div class="overflow-hidden rounded-xl border border-gray-200 dark:border-[#191e3a]">
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs text-gray-500 dark:bg-white/5">
              <tr>
                <th class="px-3 py-2 text-left font-medium">Field</th>
                @if (snapshotKind !== 'create') { <th class="px-3 py-2 text-left font-medium">Before</th> }
                @if (snapshotKind !== 'delete') { <th class="px-3 py-2 text-left font-medium">After</th> }
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-[#191e3a]">
              @for (d of visibleDiff; track d.key) {
              <tr [class.bg-amber-50/60]="d.changed && snapshotKind === 'update'" [class.dark:bg-amber-950/20]="d.changed && snapshotKind === 'update'">
                <td class="px-3 py-2 align-top font-medium text-gray-700 dark:text-gray-200">{{ d.key }}</td>
                @if (snapshotKind !== 'create') {
                <td class="max-w-[14rem] break-words px-3 py-2 align-top text-gray-500" [class.line-through]="d.changed && snapshotKind === 'update'">{{ display(d.before) }}</td>
                }
                @if (snapshotKind !== 'delete') {
                <td class="max-w-[14rem] break-words px-3 py-2 align-top" [class.font-medium]="d.changed">{{ display(d.after) }}</td>
                }
              </tr>
              }
            </tbody>
          </table>
        </div>
        @if (snapshotKind === 'update' && unchangedCount > 0) {
        <button type="button" class="mt-2 text-xs text-primary hover:underline" (click)="showAll = !showAll">
          {{ showAll ? 'Show only the ' + changed.length + ' changed' : 'Show all ' + diff.length + ' fields' }}
        </button>
        }
        }
      </section>

      <!-- Who, where -->
      <section>
        <h3 class="mb-2 text-sm font-semibold">Who and where</h3>
        <dl class="grid grid-cols-1 gap-x-6 gap-y-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2 dark:bg-white/5">
          <div>
            <dt class="text-xs text-gray-500">Performed by</dt>
            <dd class="break-all font-medium">{{ r.actor_email || (r.actor_id ? '#' + r.actor_id : 'System') }}</dd>
            <dd class="text-xs text-gray-400">{{ r.actor_type }}</dd>
          </div>
          <div>
            <dt class="text-xs text-gray-500">IP address</dt>
            <dd class="font-mono">{{ r.source_ip || '—' }}</dd>
            <dd class="text-xs text-gray-400">{{ ua.browser }} on {{ ua.platform }}</dd>
          </div>
          <div>
            <dt class="text-xs text-gray-500">Request</dt>
            <dd class="break-all font-mono text-xs">{{ r.http_method || '—' }} {{ r.http_path || '' }}</dd>
          </div>
          <div>
            <dt class="text-xs text-gray-500">Recorded by</dt>
            <dd>{{ r.source }}<span *ngIf="r.tenant_id" class="text-gray-400"> · tenant {{ r.tenant_id }}</span></dd>
          </div>
        </dl>
      </section>

      <!-- Integrity (folded) -->
      <section>
        <button type="button" class="flex w-full items-center justify-between text-sm font-semibold" (click)="integrityOpen = !integrityOpen" [attr.aria-expanded]="integrityOpen">
          Integrity
          <span class="text-xs font-normal text-gray-400">entry #{{ r.id }} · {{ integrityOpen ? 'hide' : 'show' }}</span>
        </button>
        @if (integrityOpen) {
        <dl class="mt-2 space-y-2 rounded-xl bg-gray-50 p-4 text-xs dark:bg-white/5">
          <div><dt class="text-gray-500">Entry hash</dt><dd class="break-all font-mono">{{ r.hash }}</dd></div>
          <div><dt class="text-gray-500">Links to previous</dt><dd class="break-all font-mono">{{ r.prev_hash || 'first entry' }}</dd></div>
          <div><dt class="text-gray-500">Request ID</dt><dd class="break-all font-mono">{{ r.request_id || '—' }}</dd></div>
          <div><dt class="text-gray-500">Action code</dt><dd class="font-mono">{{ r.action }}</dd></div>
        </dl>
        }
      </section>

      <section>
        <button type="button" class="text-xs text-primary hover:underline" (click)="rawOpen = !rawOpen">
          {{ rawOpen ? 'Hide raw data' : 'Show raw data' }}
        </button>
        @if (rawOpen) {
        <pre class="mt-2 max-h-80 overflow-auto rounded-xl bg-black/5 p-3 text-[11px] dark:bg-black/30">{{ rawMetadata }}</pre>
        }
      </section>
    </div>
  </aside>
</dialog>
}
